import React, { useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { generateUUID } from '@renderer/utils/idUtils'
import BkScreen from './BkScreen'
import { useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import BkStrip from './BkStrip'
import { BK_INK } from './BkPixel'
import { BK_HIT, BK_STICKER_GAP, BK_STICKER_GRID, bkHit, stripBox } from './layout'
import { useBkPhotos } from './useBkPhotos'
import { useBkStickerStore } from './stickers'
import { layoutSet, setScale } from '@renderer/lib/stickers/layoutSet'
import { artShape } from '@renderer/lib/stickers/artShape'
import { isScatterSet, layoutScatter, spritePieces } from './scatter'

/**
 * Screen 8 — "select your sticker".
 *
 * Each tile in the painted tray is a SET — one image of the designer's own
 * cluster — and tapping it lays that set over the strip in one go, at the
 * anchors `layoutSet` works out from the frame's photo windows. The guest
 * chooses a set, not pieces: there is nothing to drag or pinch. Tapping the
 * lit tile takes the set off; tapping another swaps it. One set per strip.
 *
 * A "scatter" set (see scatter.ts) is the exception to the three anchors: its
 * image is a sprite of pieces, and four or five of them are thrown over every
 * photo instead, clear of the face — foil stars on a photobooth strip.
 */
const Edit: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { uploadSessionState, logActivity } = useSessionStateStore()
  const sessionId = useSessionStateStore((s) => s.id)
  const frameId = useSessionStateStore((s) => s.userPreferences?.frameId)
  const { frames, stickers, config } = useConfigStore()
  const { placements, add, clear } = useBkStickerStore()
  const photos = useBkPhotos()
  /** The newest tap wins, even if an older one's image loads later. */
  const tapRef = useRef(0)

  const frame = frames.find((f) => f._id === frameId)
  const activeSet = placements[0]?.stickerId ?? null

  const toggleSet = async (sticker: { _id: string; name: string; url: string }): Promise<void> => {
    const tap = ++tapRef.current
    if (activeSet === sticker._id) {
      clear()
      logActivity(`BK sticker set removed: ${sticker.name}`)
      return
    }
    const seed = `${sessionId}:${sticker._id}`
    if (isScatterSet(sticker)) {
      let pieces: string[]
      try {
        pieces = await spritePieces(sticker.url)
      } catch (error) {
        logActivity(`BK scatter set failed to load: ${sticker.name}`, { level: 'error' })
        console.warn('[BK] scatter sprite failed:', error)
        return
      }
      if (tap !== tapRef.current) return
      clear()
      for (const stamp of layoutScatter(frame?.drawParams, pieces.length, seed)) {
        add({
          id: generateUUID(),
          stickerId: sticker._id,
          url: pieces[stamp.piece],
          xPct: stamp.xPct,
          yPct: stamp.yPct,
          widthPct: stamp.widthPct,
          rotation: stamp.rotation
        })
      }
      logActivity(`BK sticker set chosen: ${sticker.name}`)
      return
    }
    const { ratio, coverage } = await artShape(sticker.url)
    if (tap !== tapRef.current) return
    clear()
    for (const stamp of layoutSet(frame?.drawParams, ratio, seed, setScale(coverage))) {
      add({ id: generateUUID(), stickerId: sticker._id, url: sticker.url, ...stamp })
    }
    logActivity(`BK sticker set chosen: ${sticker.name}`)
  }

  const confirm = (): void => {
    logActivity(`BK stickers confirmed (${activeSet ? 'one set' : 'none'})`, { to: 'select' })
    uploadSessionState()
    navigate(`/${projectId}/select`)
  }
  const remaining = useBkPageTimeout(config?.pages?.edit?.settings?.editTimeout, confirm)

  return (
    <BkScreen plate="sticker.webp">
      <div style={bkHit(stripBox(frame?.drawParams.canvasWidth, frame?.drawParams.canvasHeight))}>
        <BkStrip frame={frame} photos={photos} stickers={placements} className="h-full w-full" />
      </div>

      <div
        style={{ ...bkHit(BK_STICKER_GRID), gap: BK_STICKER_GAP }}
        className="grid grid-cols-5 grid-rows-3"
      >
        {stickers.slice(0, 15).map((sticker) => {
          const on = activeSet === sticker._id
          return (
            <button
              key={sticker._id}
              aria-label={on ? `Remove ${sticker.name}` : `Add ${sticker.name}`}
              aria-pressed={on}
              onClick={() => void toggleSet(sticker)}
              className="flex items-center justify-center bg-transparent p-2"
              // The chosen tile is stitched round in the lettering ink, as the
              // chosen print note is on the payment page.
              style={on ? { outline: `3px dashed ${BK_INK}`, outlineOffset: -7 } : undefined}
            >
              <img
                src={sticker.thumbnail || sticker.url}
                alt=""
                className="max-h-full max-w-full object-contain"
              />
            </button>
          )
        })}
      </div>

      <button
        aria-label="Confirm"
        onClick={confirm}
        style={bkHit(BK_HIT.edit.confirm)}
        className="cursor-pointer bg-transparent"
      />
      <BkTimer seconds={remaining} />
    </BkScreen>
  )
}

export default Edit
